import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import PageHeader from "../../componentes/PageHeader";
import GradientBanner from "../../componentes/Banner";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "../../styles/circuit-background.css";
import {
  faShieldAlt,
  faLock,
  faBug,
  faNetworkWired,
  faKey,
  faFish,
  faUsers,
  faServer,
  faBuilding,
  faFileAlt,
  faGlobe,
  faDatabase,
} from "@fortawesome/free-solid-svg-icons";
import { FullscreenSpinner } from "../../componentes/LoadingIndicator";
import { fetchAwareness } from "../../api/awareness";

const SecurityCard = ({ icon, title, description, bulletPoints, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    whileHover={{
      y: -8,
      boxShadow:
        "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    }}
    className="bg-white p-8 rounded-xl shadow-lg transition-all duration-300 border-b-4 border-primary overflow-hidden group relative"
  >
    <div className="absolute -right-10 -top-10 w-20 h-20 bg-gradient-to-br from-primary/30 to-primary/5 rounded-full"></div>
    <div className="flex items-start relative z-10">
      <div className="bg-primary p-4 rounded-lg text-white transform transition-transform group-hover:rotate-6 group-hover:scale-110 flex-shrink-0">
        <FontAwesomeIcon icon={icon} className="text-2xl" />
      </div>
      <div className="ml-5">
        <h3 className="text-h-6 font-bold text-heading mb-3 group-hover:text-primary transition-colors font-nunito-sans">
          {title}
        </h3>
        <p className="text-body-big text-paragraph leading-relaxed font-catamaran">
          {description}
        </p>
        <ul className="mt-4 space-y-2">
          {bulletPoints.map((point, index) => (
            <li key={index} className="flex items-center text-accent">
              <span className="w-1.5 h-1.5 bg-primary rounded-full mr-2"></span>
              <span className="text-body font-nunito-sans text-paragraph">
                {point}
              </span>
            </li>
          ))}
        </ul>
        <motion.div
          initial={{ opacity: 0, width: 0 }}
          whileHover={{ opacity: 1, width: "100%" }}
          className="h-0.5 bg-primary/20 mt-4"
        ></motion.div>
      </div>
    </div>
  </motion.div>
);

const PublicPrivateTraining = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const threatIcons = {
    "Targeted Phishing Attacks": faFish,
    "Insider Threats": faServer,
    "Data Breaches": faDatabase,
    "Social Engineering": faUsers,
    "Network Intrusions": faNetworkWired,
    "Regulatory Violations": faFileAlt,
    "Malware Infections": faBug,
    "DDoS Disruptions": faGlobe,
  };

  const { data: threats = [], isLoading, isError, error } = useQuery({
    queryKey: ["threats", "PublicPrivate"],
    queryFn: () => fetchAwareness({ lang: "en", group: "PublicPrivate" }),
  });

  const securityMeasures = [
    {
      icon: faBuilding,
      title: t("awareness.publicprivate.policy.title", "Compliance with Policies"),
      description: t(
        "awareness.publicprivate.policy.description",
        "Adhere to organizational and regulatory cybersecurity standards."
      ),
      bulletPoints: [
        t(
          "awareness.publicprivate.policy.bulletPoints.training",
          "Complete mandatory security training"
        ),
        t(
          "awareness.publicprivate.policy.bulletPoints.report",
          "Report incidents per protocol"
        ),
        t(
          "awareness.publicprivate.policy.bulletPoints.audit",
          "Participate in regular security audits"
        ),
      ],
    },
    {
      icon: faFileAlt,
      title: t("awareness.publicprivate.data.title", "Sensitive Data Handling"),
      description: t(
        "awareness.publicprivate.data.description",
        "Protect confidential information in public and private sector workflows."
      ),
      bulletPoints: [
        t(
          "awareness.publicprivate.data.bulletPoints.encrypt",
          "Encrypt sensitive files before sharing"
        ),
        t(
          "awareness.publicprivate.data.bulletPoints.access",
          "Limit data access to authorized personnel"
        ),
        t(
          "awareness.publicprivate.data.bulletPoints.dispose",
          "Securely dispose of outdated records"
        ),
      ],
    },
    {
      icon: faGlobe,
      title: t("awareness.publicprivate.network.title", "Secure Network Access"),
      description: t(
        "awareness.publicprivate.network.description",
        "Ensure safe connections for internal and external operations."
      ),
      bulletPoints: [
        t(
          "awareness.publicprivate.network.bulletPoints.vpn",
          "Use VPNs for remote access to networks"
        ),
        t(
          "awareness.publicprivate.network.bulletPoints.firewall",
          "Maintain updated firewalls"
        ),
        t(
          "awareness.publicprivate.network.bulletPoints.monitor",
          "Monitor network traffic for anomalies"
        ),
      ],
    },
    {
      icon: faShieldAlt,
      title: t("awareness.publicprivate.device.title", "Endpoint Security"),
      description: t(
        "awareness.publicprivate.device.description",
        "Secure all devices used in public and private sector environments."
      ),
      bulletPoints: [
        t(
          "awareness.publicprivate.device.bulletPoints.antivirus",
          "Install enterprise-grade antivirus"
        ),
        t(
          "awareness.publicprivate.device.bulletPoints.updates",
          "Keep software and firmware updated"
        ),
        t(
          "awareness.publicprivate.device.bulletPoints.lock",
          "Enforce device locking policies"
        ),
      ],
    },
  ];

  const introductionSection = [
    {
      title: t(
        "awareness.publicprivate.main.title",
        "Cybersecurity for Public & Private Sectors"
      ),
      description: t(
        "awareness.publicprivate.main.description",
        "Employees in public and private sectors handle sensitive data daily. Learn to secure your organization against cyber threats."
      ),
    },
  ];

  const riskSection = [
    {
      title: t("awareness.publicprivate.risks.title", "Sector-Specific Cyber Risks"),
      description: t(
        "awareness.publicprivate.risks.description",
        "Explore threats unique to public and private sector operations and how to counter them."
      ),
    },
  ];

  const cyberRisks = [
    {
      icon: faFish,
      title: t("awareness.publicprivate.phishing.title", "Targeted Phishing Attacks"),
      description: t(
        "awareness.publicprivate.phishing.description",
        "Attackers target employees to gain access to sensitive systems."
      ),
      tips: [
        t(
          "awareness.publicprivate.phishing.tips.tip1",
          "Verify requests for credentials or funds"
        ),
        t(
          "awareness.publicprivate.phishing.tips.tip2",
          "Use email filters to flag suspicious messages"
        ),
        t(
          "awareness.publicprivate.phishing.tips.tip3",
          "Educate staff on phishing red flags"
        ),
      ],
    },
    {
      icon: faServer,
      title: t("awareness.publicprivate.insider.title", "Insider Threats"),
      description: t(
        "awareness.publicprivate.insider.description",
        "Risks from employees or contractors mishandling data or systems."
      ),
      tips: [
        t(
          "awareness.publicprivate.insider.tips.tip1",
          "Implement strict access controls"
        ),
        t(
          "awareness.publicprivate.insider.tips.tip2",
          "Monitor user activity for unusual behavior"
        ),
        t(
          "awareness.publicprivate.insider.tips.tip3",
          "Conduct regular employee background checks"
        ),
      ],
    },
  ];

  if (isLoading) {
    return (
      <>
        <PageHeader />
        <GradientBanner
          title={t(
            "awareness.publicprivate.title",
            "Public & Private Sector Cybersecurity Training"
          )}
          description={t(
            "awareness.publicprivate.subtitle",
            "Strengthening security for employees in public and private organizations"
          )}
          path={[
            { label: t("header.home", "Home"), link: "/" },
            { label: t("header.publicprivate", "Public & Private"), link: null },
          ]}
          backgroundIcons={[faBuilding, faFileAlt, faGlobe, faShieldAlt, faLock, faKey]}
        />
        <FullscreenSpinner className="py-12" />
      </>
    );
  }

  return (
    <>
      <PageHeader />
      <GradientBanner
        title={t(
          "awareness.publicprivate.title",
          "Public & Private Sector Cybersecurity Training"
        )}
        description={t(
          "awareness.publicprivate.subtitle",
          "Strengthening security for employees in public and private organizations"
        )}
        path={[
          { label: t("header.home", "Home"), link: "/" },
          { label: t("header.publicprivate", "Public & Private"), link: null },
        ]}
        backgroundIcons={[faBuilding, faFileAlt, faGlobe, faShieldAlt, faLock, faKey]}
      />
      <div className="circuit-page-background">
        <div className="max-xl:px-4 py-8 md:py-16 px-dynamic flex flex-col md:flex-row gap-8">
          {/* Main Content */}
          <div className="flex-1">
            {introductionSection.map((introduction, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="border-l-4 border-primary items-start bg-white pb-4 pt-1 pr-8 mb-4 mt-4 rounded-md transition-all duration-300 overflow-hidden group relative"
              >
                <div className="flex items-start relative z-10">
                  <div className="ml-5">
                    <h3 className="text-h-3 lg:text-3xl font-bold text-heading mb-3 transition-colors font-nunito-sans">
                      {introduction.title}
                    </h3>
                    <p className="text-body-big text-paragraph leading-relaxed font-catamaran">
                      {introduction.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}

            <div className="grid lg:grid-cols-2 gap-6 mb-12">
              {securityMeasures.map((measure, index) => (
                <SecurityCard
                  key={index}
                  icon={measure.icon}
                  title={measure.title}
                  description={measure.description}
                  bulletPoints={measure.bulletPoints}
                  delay={index * 0.1}
                />
              ))}
            </div>

            <section className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-primary-light/30 to-blue-50 opacity-70 rounded-3xl -z-10"></div>
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-primary-light/50 rounded-full opacity-30 blur-2xl -z-10"></div>
              <div className="absolute -bottom-10 -left-10 w-60 h-60 bg-blue-100 rounded-full opacity-40 blur-3xl -z-10"></div>

              <div className="relative md:mt-24">
                {riskSection.map((risks, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="border-l-4 border-primary items-start bg-white pb-8 pt-1 pr-8 mb-8 rounded-md transition-all duration-300 overflow-hidden group relative"
                  >
                    <div className="flex items-start relative z-10">
                      <div className="ml-5">
                        <h3 className="text-h-3 lg:text-3xl font-bold text-heading mb-3 transition-colors font-nunito-sans">
                          {risks.title}
                        </h3>
                        <p className="text-body-big text-paragraph leading-relaxed font-catamaran">
                          {risks.description}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}

                <div className="grid md:grid-cols-2 gap-10 mb-10">
                  {cyberRisks.map((risk, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 + index * 0.2 }}
                      className="relative"
                    >
                      <div className="bg-white rounded-xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow duration-300 group">
                        <div className="bg-primary p-6 flex items-center relative overflow-hidden">
                          <div className="absolute right-0 top-0 w-32 h-32 bg-white/10 rounded-full -mr-10 -mt-10"></div>
                          <div className="absolute left-0 bottom-0 w-16 h-16 bg-white/10 rounded-full -ml-5 -mb-5"></div>
                          <div className="bg-white/20 p-3 rounded-full backdrop-blur-sm mr-4">
                            <FontAwesomeIcon
                              icon={risk.icon}
                              className="text-h-6 text-white"
                            />
                          </div>
                          <h3 className="text-h-6 font-bold text-white font-nunito-sans">
                            {risk.title}
                          </h3>
                        </div>
                        <div className="p-6">
                          <p className="text-body-big text-paragraph leading-relaxed font-nunito-sans">
                            {risk.description}
                          </p>
                          <div>
                            <h4 className="font-bold text-gray-800 mb-3 mt-3 flex items-center font-nunito-sans">
                              <FontAwesomeIcon
                                icon={faShieldAlt}
                                className="text-primary mr-2"
                              />
                              {t(
                                "awareness.publicprivate.risks.safety_measures",
                                "Safety Measures"
                              )}
                            </h4>
                            <ul className="space-y-3">
                              {risk.tips.map((tip, idx) => (
                                <motion.li
                                  key={idx}
                                  initial={{ opacity: 0, x: -10 }}
                                  whileInView={{ opacity: 1, x: 0 }}
                                  transition={{ delay: idx * 0.1 }}
                                  viewport={{ once: true }}
                                  className="flex items-start group/tip"
                                >
                                  <div className="bg-primary/10 text-primary rounded-full w-6 h-6 flex items-center justify-center mt-0.5 mr-3 group-hover/tip:bg-primary group-hover/tip:text-white transition-colors">
                                    <span className="text-body font-bold">
                                      {idx + 1}
                                    </span>
                                  </div>
                                  <span className="text-paragraph text-body group-hover/tip:text-gray-900 transition-colors font-nunito-sans">
                                    {tip}
                                  </span>
                                </motion.li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <div className="md:w-[30%]">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="bg-gradient-to-br from-white to-blue-50 rounded-2xl shadow-2xl md:p-8 relative overflow-hidden sticky top-24 max-xl:top-32"
            >
              <div className="absolute top-0 right-0 opacity-10">
                <svg
                  width="300"
                  height="300"
                  viewBox="0 0 200 200"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill="#4F46E5"
                    d="M40,0 L60,0 L100,40 L40,100 L0,60 Z"
                  />
                  <circle cx="150" cy="50" r="30" fill="#4F46E5" />
                  <rect x="120" y="100" width="60" height="60" fill="#4F46E5" />
                </svg>
              </div>

              <div className="flex items-start mb-10 relative z-10 border-l-4 border-primary rounded-md">
                <div className="ml-5">
                  <h2 className="lg:text-3xl text-h-3 max-xl:text-2xl font-bold text-heading font-catamaran">
                    {t(
                      "awareness.publicprivate.awareness.title",
                      "Sector Cyber Awareness"
                    )}
                  </h2>
                  <p className="text-gray-600 mt-1 font-nunito-sans">
                    {t(
                      "awareness.publicprivate.awareness.subtitle",
                      "Key threats for public and private employees"
                    )}
                  </p>
                </div>
              </div>

              <div className="max-md:p-4 grid md:grid-cols-1 gap-6 relative z-10">
                {threats.length > 0 ? (
                  threats.map((item) => (
                    <motion.div
                      key={`${item.group}-${item.threat_type[0]}`}
                      whileHover={{ y: -5 }}
                      className="flex items-center p-4 bg-white rounded-xl shadow-md transition-all duration-300 cursor-pointer"
                      onClick={() =>
                        navigate(
                          `/awareness-detail/${item.group}/${item.threat_type[0]}`
                        )
                      }
                    >
                      <FontAwesomeIcon
                        icon={threatIcons[item.threat_type[0]] || faBug}
                        className="text-primary text-xl mr-3"
                      />
                      <span className="text-body text-paragraph font-nunito-sans">
                        {item.threat_type[0]}
                      </span>
                    </motion.div>
                  ))
                ) : (
                  <p>No threats available for this group.</p>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PublicPrivateTraining;